/**
 * DateTimePicker — cross-platform date + time picker.
 *
 * Value contract: a wall-clock string "YYYY-MM-DDTHH:mm" with NO timezone.
 * The digits are interpreted by the caller/backend as the OWNER's local
 * timezone, so the picker never has to know the owner's IANA zone — it just
 * edits wall-clock parts. (Backend converts the wall clock to a UTC instant
 * in the owner's tz.)
 *
 * - Web: renders a native <input type="datetime-local"> (react-native-web maps
 *   the `type` prop straight through to the DOM input), whose value format is
 *   already "YYYY-MM-DDTHH:mm".
 * - Native: a tappable field opens a modal with a month calendar + hour/minute
 *   steppers. Pure JS, no native module — works without an app rebuild.
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

interface Props {
  /** Wall-clock value — "YYYY-MM-DDTHH:mm" in datetime mode, "YYYY-MM-DD" in date mode (owner-local). */
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label?: string;
  /** 'datetime' (default) shows a time picker; 'date' is calendar-only and emits "YYYY-MM-DD". */
  readonly mode?: 'datetime' | 'date';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const pad = (n: number) => String(n).padStart(2, '0');

interface Parts { y: number; mo: number; d: number; h: number; mi: number; }

function parse(v: string): Parts {
  // Time portion is optional so the same parser handles "YYYY-MM-DD" (date mode).
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(v || '');
  if (!m) {
    const now = new Date();
    return { y: now.getFullYear(), mo: now.getMonth() + 1, d: now.getDate(), h: now.getHours(), mi: now.getMinutes() };
  }
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] ? +m[4] : 0, mi: m[5] ? +m[5] : 0 };
}

function build(p: Parts): string {
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

function buildDate(p: Parts): string {
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
}

function daysInMonth(y: number, mo: number): number {
  return new Date(y, mo, 0).getDate();
}

/** Day-of-week (0=Sun) of the 1st of the month. */
function firstDow(y: number, mo: number): number {
  return new Date(y, mo - 1, 1).getDay();
}

/** Pretty label like "May 1, 2026 · 2:30 PM" (or just "May 1, 2026" in date mode). */
function prettyLabel(v: string, mode: 'datetime' | 'date'): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(v)) return 'Select…';
  const p = parse(v);
  const datePart = `${MONTHS[p.mo - 1]} ${p.d}, ${p.y}`;
  if (mode === 'date') return datePart;
  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  const ampm = p.h < 12 ? 'AM' : 'PM';
  return `${datePart} · ${h12}:${pad(p.mi)} ${ampm}`;
}

export default function DateTimePicker({ value, onChange, label, mode = 'datetime' }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [open, setOpen] = useState(false);
  /** Emit in the format the current mode expects. */
  const emit = (p: Parts) => onChange(mode === 'date' ? buildDate(p) : build(p));
  // Month being viewed in the calendar (defaults to the value's month).
  const [view, setView] = useState<{ y: number; mo: number }>(() => {
    const p = parse(value);
    return { y: p.y, mo: p.mo };
  });

  // --- Web: native datetime-local input ---
  if (Platform.OS === 'web') {
    // Render a real DOM <input type="datetime-local"> via react-dom rather than
    // a react-native-web TextInput. RNW's controlled-value re-sync fights the
    // browser's segmented datetime field and resets focus after a keystroke
    // (the cursor gets "kicked out"); a plain controlled DOM input does not.
    const webStyle: Record<string, any> = {
      backgroundColor: colors.surfaceSecondary,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      padding: spacing.sm,
      fontSize: fontSize.sm,
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    };
    return (
      <View style={{ flex: 1 }}>
        {label ? <Text style={styles.fieldLabel}>{label}</Text> : null}
        {React.createElement('input', {
          type: mode === 'date' ? 'date' : 'datetime-local',
          value,
          onChange: (e: any) => onChange(e.target.value),
          // 1-minute granularity in browser time pickers (matches the native stepper).
          ...(mode !== 'date' ? { step: 60 } : {}),
          style: webStyle,
        })}
      </View>
    );
  }

  // --- Native: tappable field + modal calendar/time ---
  const p = parse(value);

  const setPart = (patch: Partial<Parts>) => emit({ ...parse(value), ...patch });

  const bump = (field: 'h' | 'mi', delta: number) => {
    const cur = parse(value);
    if (field === 'h') cur.h = (cur.h + delta + 24) % 24;
    else cur.mi = (cur.mi + delta + 60) % 60;
    emit(cur);
  };

  const shiftMonth = (delta: number) => {
    setView(prev => {
      let mo = prev.mo + delta;
      let y = prev.y;
      if (mo < 1) { mo = 12; y--; }
      if (mo > 12) { mo = 1; y++; }
      return { y, mo };
    });
  };

  const dim = daysInMonth(view.y, view.mo);
  const lead = firstDow(view.y, view.mo);
  const cells: (number | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  const ampm = p.h < 12 ? 'AM' : 'PM';

  return (
    <View style={{ flex: 1 }}>
      {label ? <Text style={styles.fieldLabel}>{label}</Text> : null}
      <TouchableOpacity style={styles.field} onPress={() => { setView({ y: p.y, mo: p.mo }); setOpen(true); }}>
        <Text style={styles.fieldText}>{prettyLabel(value, mode)}</Text>
        <Ionicons name="calendar-outline" size={18} color={colors.textLight} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setOpen(false)}>
          <TouchableOpacity activeOpacity={1} style={[styles.sheet, { paddingBottom: modalBottomPadding + spacing.md }]}>
            {/* Month header */}
            <View style={styles.monthHeader}>
              <TouchableOpacity onPress={() => shiftMonth(-1)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="chevron-back" size={22} color={colors.text} />
              </TouchableOpacity>
              <Text style={styles.monthTitle}>{MONTHS_LONG[view.mo - 1]} {view.y}</Text>
              <TouchableOpacity onPress={() => shiftMonth(1)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="chevron-forward" size={22} color={colors.text} />
              </TouchableOpacity>
            </View>

            {/* Weekday row */}
            <View style={styles.weekRow}>
              {DOW.map((d, i) => <Text key={i} style={styles.weekday}>{d}</Text>)}
            </View>

            {/* Day grid */}
            <View style={styles.grid}>
              {cells.map((d, i) => {
                const selected = d !== null && d === p.d && view.y === p.y && view.mo === p.mo;
                return (
                  <TouchableOpacity
                    key={i}
                    style={[styles.cell, selected && styles.cellSelected]}
                    disabled={d === null}
                    onPress={() => d !== null && setPart({ y: view.y, mo: view.mo, d })}
                  >
                    <Text style={[styles.cellText, selected && styles.cellTextSelected, d === null && { opacity: 0 }]}>
                      {d ?? 0}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* Time steppers (datetime mode only) */}
            {mode !== 'date' && <View style={styles.timeRow}>
              <Text style={styles.timeLabel}>Time</Text>
              <View style={styles.stepper}>
                <TouchableOpacity onPress={() => bump('h', -1)} style={styles.stepBtn}><Ionicons name="remove" size={18} color={colors.primary} /></TouchableOpacity>
                <Text style={styles.timeVal}>{pad(h12)}</Text>
                <TouchableOpacity onPress={() => bump('h', 1)} style={styles.stepBtn}><Ionicons name="add" size={18} color={colors.primary} /></TouchableOpacity>
              </View>
              <Text style={styles.colon}>:</Text>
              <View style={styles.stepper}>
                <TouchableOpacity onPress={() => bump('mi', -1)} style={styles.stepBtn}><Ionicons name="remove" size={18} color={colors.primary} /></TouchableOpacity>
                <Text style={styles.timeVal}>{pad(p.mi)}</Text>
                <TouchableOpacity onPress={() => bump('mi', 1)} style={styles.stepBtn}><Ionicons name="add" size={18} color={colors.primary} /></TouchableOpacity>
              </View>
              <TouchableOpacity
                style={styles.ampmBtn}
                onPress={() => setPart({ h: (p.h + 12) % 24 })}
              >
                <Text style={styles.ampmText}>{ampm}</Text>
              </TouchableOpacity>
            </View>}

            <TouchableOpacity style={styles.doneBtn} onPress={() => setOpen(false)}>
              <Text style={styles.doneText}>Done</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  input: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
  },
  field: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    borderWidth: 1, borderColor: c.border,
  },
  fieldText: { fontSize: fontSize.sm, color: c.text },
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  sheet: {
    backgroundColor: c.surface, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg,
    padding: spacing.md,
  },
  monthHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  monthTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  weekRow: { flexDirection: 'row' },
  weekday: { flex: 1, textAlign: 'center', fontSize: fontSize.xs, color: c.textLight, marginBottom: spacing.xs },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: `${100 / 7}%`, aspectRatio: 1.4, alignItems: 'center', justifyContent: 'center' },
  cellSelected: { },
  cellText: { fontSize: fontSize.sm, color: c.text, width: 32, height: 32, lineHeight: 32, textAlign: 'center', borderRadius: 16, overflow: 'hidden' },
  cellTextSelected: { backgroundColor: c.primary, color: c.white, fontWeight: fontWeight.bold },
  timeRow: { flexDirection: 'row', alignItems: 'center', marginTop: spacing.md, gap: spacing.xs },
  timeLabel: { fontSize: fontSize.sm, color: c.text, marginRight: spacing.sm },
  stepper: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm },
  stepBtn: { padding: spacing.xs },
  timeVal: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, minWidth: 26, textAlign: 'center' },
  colon: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  ampmBtn: { marginLeft: spacing.sm, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.primary },
  ampmText: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.primary },
  doneBtn: { marginTop: spacing.md, backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingVertical: spacing.sm, alignItems: 'center' },
  doneText: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.white },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
